import { EucProject, Origin, ProjectLifecycle } from '../types';
import { BUS_TOPIC_SGCONNECT_USERINFO, getUserInfo, getWidgetBus } from './helpers';

interface SgConnectUserConstraint {
  name: string;
  values: string[];
}

interface SgConnectUserPermission {
  name: string;
  constraints: SgConnectUserConstraint[];
}

interface SgConnectUserAuthorization {
  resource: string;
  resource_id: string;
  permissions: SgConnectUserPermission[];
}

export interface SgConnectUser {
  mail: string;
  name: string;
  first_name: string;
  last_name: string;
  sub: string;
  igg: string;
  is_sg_group_user: string;
  rc_local_sigle: string;
  user_authorization: SgConnectUserAuthorization[];
  origin_network: 'LAN' | 'Internet';
}

const IAM_RESOURCE_ID = 'api.euc-catalogue';

export enum UserPermission {
  ADMINISTRATION_PERMISSION = 'administration',
  EDITION_PERMISSION = 'edition',
  RISK_MANAGER_PERMISSION = 'risk manager',
}

export function hasUserPermission(user: SgConnectUser, permission: UserPermission): boolean {
  return user.user_authorization.some((autho) => {
    return autho.resource === IAM_RESOURCE_ID && autho.permissions.some((perm) => perm.name === permission);
  });
}

export const hasPermission = async (permission: UserPermission): Promise<boolean> => {
  const bus = getWidgetBus();
  const user: SgConnectUser | undefined = bus.dangerouslyGetCurrentValue(BUS_TOPIC_SGCONNECT_USERINFO);
  if (!!user) {
    return hasUserPermission(user, UserPermission.ADMINISTRATION_PERMISSION);
  }
  return new Promise((resolve) => {
    const handle = bus.subscribe(BUS_TOPIC_SGCONNECT_USERINFO, (user: SgConnectUser | null | undefined) => {
      if (!!user) {
        bus.unsubscribe(handle);
        return resolve(hasUserPermission(user, permission));
      }
    });
  });
};

export const hasPermissionSync = (permission: UserPermission): boolean => {
  const bus = getWidgetBus();
  const user: SgConnectUser | undefined = bus.dangerouslyGetCurrentValue(BUS_TOPIC_SGCONNECT_USERINFO);
  return !!user && hasUserPermission(user, permission);
};

export const hasCertificationRights = (project: EucProject): boolean => {
  const user = getUserInfo();
  if (!user) {
    return false;
  }
  const { mail } = user;
  const readOnly = isReadOnlyEUC(project);
  return project.owner === mail && !readOnly;
};

export const hasValidationRights = (project: EucProject): boolean => {
  const user = getUserInfo();
  if (!user) {
    return false;
  }
  const { mail } = user;
  const readOnly = isReadOnlyEUC(project);
  return (project.managerBackups.includes(mail) || project.ownerManager === mail) && !readOnly;
};

// Check if the user is part of the EUC, for edition checks.
const userPartOfEUC = (project: EucProject, { mail }: SgConnectUser): boolean => {
  const editor = hasPermissionSync(UserPermission.EDITION_PERMISSION);
  if (editor) {
    return true;
  }
  return (
    project.owner === mail ||
    project.ownerManager === mail ||
    project.managerBackups.includes(mail) ||
    project.citizenDevelopers.includes(mail)
  );
};

export const hasEditionRightsOnEuc = (project: EucProject): boolean => {
  const user = getUserInfo();
  if (!user) {
    return false;
  }
  const readOnly = isReadOnlyEUC(project);
  const orignsWithoutReadOnly = [Origin.PROJECT, Origin.UNMANAGED];

  if (window.globalConfiguration.eudaSendDocumentation) {
    orignsWithoutReadOnly.push(Origin.EUDA);
  }

  return userPartOfEUC(project, user) && (!readOnly || orignsWithoutReadOnly.includes(project.origin));
};

export const hasChangeOwnerRights = (project: EucProject): boolean => {
  const user = getUserInfo();
  return !!user && userPartOfEUC(project, user);
};

export const isReadOnlyEUC = (project: EucProject): boolean => {
  return project.lifecycle === ProjectLifecycle.DECOMMISSIONED;
};

export const isLinkeable = (project: EucProject): boolean => {
  return !isReadOnlyEUC(project);
};
